import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { DatePicker } from "@/components/design/DatePicker";
import type { ListingState } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { fromLocalDateTimeInput, toLocalDateTimeInput } from "./listing-form";
import {
  availableTransitions,
  deadlinePassed,
  LISTING_TRANSITION_CONSEQUENCES,
  LISTING_TRANSITION_LABELS,
  type ListingActions,
  type ListingTransitionSpec,
} from "./listing-state";

// Die Zustandswechsel, die genesis für diese Ausschreibung meldet, als Knöpfe.
//
// Öffnen mit abgelaufener Frist fragt erst nach einer neuen Frist und schickt sie in DERSELBEN
// Anfrage mit: genesis lehnt das Öffnen sonst ab, weil die alte Frist die Ausschreibung sofort wieder
// schlösse. Die Uhr des Browsers entscheidet dabei nur, ob gefragt wird; ob der Wechsel gilt,
// entscheidet genesis, und eine Ablehnung landet als Meldung hier.
export function ListingStateActions({
  actions,
  deadline,
  onTransition,
}: {
  actions: ListingActions;
  deadline: string | null | undefined;
  onTransition: (target: ListingState, deadline?: string | null) => Promise<void>;
}) {
  const intl = useIntl();
  const specs = availableTransitions(actions);
  const [confirming, setConfirming] = useState<ListingTransitionSpec | null>(null);
  const [rescheduling, setRescheduling] = useState<ListingTransitionSpec | null>(null);
  const [newDeadline, setNewDeadline] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (specs.length === 0) return null;

  async function run(spec: ListingTransitionSpec, movedDeadline?: string | null) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await onTransition(spec.target, movedDeadline);
      setConfirming(null);
      setRescheduling(null);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setPending(false);
    }
  }

  function start(spec: ListingTransitionSpec) {
    setError(null);
    if (spec.opens && deadlinePassed(deadline)) {
      setNewDeadline("");
      setRescheduling(spec);
    } else if (LISTING_TRANSITION_CONSEQUENCES[spec.transition]) {
      setConfirming(spec);
    } else {
      void run(spec);
    }
  }

  const movedDeadline = fromLocalDateTimeInput(newDeadline);
  const movedDeadlineValid = movedDeadline !== null && !deadlinePassed(movedDeadline);
  const errorLine = error ? (
    <p role="alert" className="mt-3 text-smycles-meta text-ink-accent">
      {error}
    </p>
  ) : null;

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {specs.map((spec, index) => (
          <Button
            key={spec.transition}
            id={`listing-transition-${spec.transition}`}
            variant={index === 0 ? "primary" : "secondary"}
            size="compact"
            disabled={pending}
            onClick={() => start(spec)}
          >
            {intl.formatMessage(LISTING_TRANSITION_LABELS[spec.transition])}
          </Button>
        ))}
      </div>
      {!confirming && !rescheduling ? errorLine : null}

      <ConfirmDialog
        id="listing-transition-dialog"
        open={confirming !== null}
        destructive={false}
        title={confirming ? intl.formatMessage(LISTING_TRANSITION_LABELS[confirming.transition]) : null}
        message={
          confirming ? (
            <>
              <p>{intl.formatMessage(LISTING_TRANSITION_CONSEQUENCES[confirming.transition]!)}</p>
              {errorLine}
            </>
          ) : null
        }
        confirmLabel={confirming ? intl.formatMessage(LISTING_TRANSITION_LABELS[confirming.transition]) : null}
        onConfirm={() => (confirming ? run(confirming) : undefined)}
        onCancel={() => {
          if (pending) return;
          setConfirming(null);
          setError(null);
        }}
      />

      <Modal
        open={rescheduling !== null}
        onClose={() => {
          if (pending) return;
          setRescheduling(null);
          setError(null);
        }}
        title={<FormattedMessage id="share.listings.deadlineDialog.title" defaultMessage="Neue Frist" />}
        footer={
          <>
            <Button variant="secondary" disabled={pending} onClick={() => setRescheduling(null)}>
              <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
            </Button>
            <Button
              disabled={pending || !movedDeadlineValid}
              onClick={() => (rescheduling ? void run(rescheduling, movedDeadline) : undefined)}
            >
              {rescheduling ? intl.formatMessage(LISTING_TRANSITION_LABELS[rescheduling.transition]) : null}
            </Button>
          </>
        }
      >
        <p className="text-smycles-body text-ink">
          <FormattedMessage
            id="share.listings.deadlineDialog.body"
            defaultMessage="Die Frist ist abgelaufen. Lege eine neue Frist in der Zukunft fest, sonst schlösse sie die Ausschreibung sofort wieder."
          />
        </p>
        <label className="mt-4 block">
          <span className="smycles-field-label">
            <FormattedMessage id="share.listings.field.deadline" defaultMessage="Frist" />
          </span>
          <DatePicker
            id="listing-deadline-new"
            aria-label={intl.formatMessage({ id: "share.listings.field.deadline" })}
            mode="datetime-local"
            className="mt-1"
            min={toLocalDateTimeInput(new Date().toISOString())}
            value={newDeadline}
            onChange={setNewDeadline}
          />
        </label>
        {newDeadline && !movedDeadlineValid ? (
          <p className="mt-2 text-smycles-meta text-ink-muted">
            <FormattedMessage
              id="share.listings.deadlineDialog.future"
              defaultMessage="Die Frist muss in der Zukunft liegen."
            />
          </p>
        ) : null}
        {errorLine}
      </Modal>
    </div>
  );
}
